import ProductTagsRenderer from '@components/Product/ProductTagsRenderer';
import { CatalogItemRelations } from '@components/Product/components/ProductCard/types/productCard.types';
import ProductCardV2Colors from '@components/Product/components/ProductCardV2/components/ProductCardV2Colors';
import ProductCardV2Heading from '@components/Product/components/ProductCardV2/components/ProductCardV2Heading';
import { ProductCardV2ColorsProps } from '@components/Product/components/ProductCardV2/components/productCardV2Colors.types';
import { PRODUCT_NAME_CLASSNAME } from '@components/Product/components/ProductCardV2/productCardV2.styles';
import ProductThumbnail from '@components/Product/components/ProductThumbnail/ProductThumbnail';
import { ProductTag } from '@components/Product/hooks/useGetProductTagGroups';
import { Box } from '@mui/material';
import { KeyboardEvent } from 'react';
import { makeStyles } from 'tss-react/mui';
import { isTouchEnabled } from '../../../../../../Theme';
import useGeneralSettingsStore from '../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import {
  CATALOG_LIST_ROW_TAB_INDEX,
  CATALOG_LIST_TAG_CHIP_HEIGHT,
  getCatalogListImageSize,
} from '../utils/catalogListRow.consts';

export interface CatalogProductListRowLeftBlockProps {
  isStacked: boolean;
  name: string;
  code: string;
  entityRelations: CatalogItemRelations;
  tags: ProductTag[] | null;
  referenceCode?: string;
  imageUrl?: string | null;
  colors?: Omit<ProductCardV2ColorsProps, 'onColorClick' | 'tabIndex'>;
  onOpenDetails?: (productColorId?: string) => void;
}

const DETAILS_TRIGGER_CLASSNAME = 'catalog-list-details-trigger';

const useStyles = makeStyles()({
  pinnedImageBox: { height: '100%', paddingTop: 0 },
});

const INFO_COLUMN_SX = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 1,
  flex: '1 1 auto',
  minWidth: 0,
} as const;

const TAGS_SLOT_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  minHeight: CATALOG_LIST_TAG_CHIP_HEIGHT,
  overflow: 'hidden',
} as const;

interface GetRootSxParams {
  nameClassName: string;
  triggerClassName: string;
  isClickable: boolean;
}

const getRootSx = ({ nameClassName, triggerClassName, isClickable }: GetRootSxParams) => ({
  width: '100%',
  display: 'flex',
  gap: 1,
  ...(isClickable && {
    [`& .${triggerClassName}`]: { cursor: 'pointer' },
    [`&:has(.${triggerClassName}:hover) .${nameClassName}`]: { color: 'primary.main' },
  }),
});

const getHeadingBlockSx = (nameClassName: string) => ({
  minWidth: 0,
  [`& .${nameClassName}`]: { WebkitLineClamp: 1 },
});

const TAG_APPEARANCE = 'default';

const ACTIVATION_KEYS = ['Enter', ' '];

const CatalogProductListRowLeftBlock = ({
  isStacked,
  name,
  code,
  entityRelations,
  tags,
  referenceCode,
  imageUrl,
  colors,
  onOpenDetails,
}: CatalogProductListRowLeftBlockProps) => {
  const { classes } = useStyles();
  const mediaFormat = useGeneralSettingsStore((state) => state.generalSettings.mediaFormat);

  const imageSize = getCatalogListImageSize({ isStacked, isTouchEnabled, mediaFormat });
  const isClickable = Boolean(onOpenDetails);

  const handleHeadingKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!ACTIVATION_KEYS.includes(event.key)) {
      return;
    }

    event.preventDefault();
    onOpenDetails?.();
  };

  const triggerClassName = isClickable ? DETAILS_TRIGGER_CLASSNAME : undefined;

  return (
    <Box
      sx={getRootSx({
        nameClassName: PRODUCT_NAME_CLASSNAME,
        triggerClassName: DETAILS_TRIGGER_CLASSNAME,
        isClickable,
      })}
    >
      <Box
        className={triggerClassName}
        sx={{ width: imageSize.width, height: imageSize.height, flexShrink: 0 }}
        onClickCapture={() => onOpenDetails?.()}
      >
        <ProductThumbnail url={imageUrl} className={classes.pinnedImageBox} />
      </Box>

      <Box sx={{ ...INFO_COLUMN_SX, minHeight: imageSize.height }}>
        {!!tags?.length && (
          <Box
            className={triggerClassName}
            sx={TAGS_SLOT_SX}
            onClickCapture={() => onOpenDetails?.()}
          >
            <ProductTagsRenderer tags={tags} variant={TAG_APPEARANCE} useSuffix />
          </Box>
        )}

        <Box
          className={triggerClassName}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? CATALOG_LIST_ROW_TAB_INDEX : undefined}
          sx={getHeadingBlockSx(PRODUCT_NAME_CLASSNAME)}
          onKeyDown={handleHeadingKeyDown}
          onClickCapture={() => onOpenDetails?.()}
        >
          <ProductCardV2Heading
            name={name}
            code={code}
            referenceCode={referenceCode}
            entityRelations={entityRelations}
            nameTestId={CatalogProductsTestIds.PRODUCT_NAME}
            codesTestId={CatalogProductsTestIds.PRODUCT_CODE}
            codesTabIndex={CATALOG_LIST_ROW_TAB_INDEX}
            showTooltipContent
          />
        </Box>

        {colors && (
          <ProductCardV2Colors
            allColors={colors.allColors}
            selectedColor={colors.selectedColor}
            tabIndex={CATALOG_LIST_ROW_TAB_INDEX}
            onColorClick={(productColorId) => onOpenDetails?.(productColorId)}
          />
        )}
      </Box>
    </Box>
  );
};

export default CatalogProductListRowLeftBlock;
